import Button from "components/buttons";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import { LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  createLegislativeDrafting,
  updateLegislativeDrafting,
} from "services/legislativeService";
import { jwtDecode } from "jwt-decode";
import { getStakeholders } from "services/getEmployees";
import { useTranslation } from "react-i18next";

interface TokenPayload {
  sub: string;
  exp: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  organization?: any;
}

type Organization = {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  organization: any;
};

const CreateLegislativeDraftingRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>();
  const [organization, setOrganization] = useState<Organization[]>([]);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    draft_instruction_summary: editData?.draft_instruction_summary || "",
    request_letter: null as File | null,
    draft_instruction_file: null as File | null,
    request_reference_number: editData?.request_reference_number || "",
  });

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: any,
    fieldName: keyof Pick<
      typeof formState,
      "request_letter" | "draft_instruction_file"
    >
  ) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        if (value instanceof File) {
          data.append(key, value);
        } else {
          data.append(key, String(value));
        }
      }
    }
    if (contextData.user?.user_type === "external")
      data.append(
        "current_status",
        LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value
      );
    else {
      data.append(
        "current_status",
        LEGISLATIVE_DRAFTING_REQUEST_STATUS.head_remark_submitted.value
      );
      data.append(
        "forward_status",
        LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value
      );
    }

    if (editData) {
      try {
        const response = await updateLegislativeDrafting(editData.id, data);
        if (response.id) {
          navigate(`/legislative-drafting/${response.id}`);
        }
        showToast(t("LegislativeDraftSentSuccess") || "Legislative draft sent successfully!", "success");
        if (fileInputRefRequestLetter.current)
          fileInputRefRequestLetter.current.value = "";
        if (fileInputRefDraftInstruction.current)
          fileInputRefDraftInstruction.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegislativeDraft") || "Failed to send Legislative draft request.", "error");
      } finally {
        setSubmitting(false);
      }
    } else {
      data.append(
        "requested_by",
        contextData.user?.user_type === "external"
          ? "stakeholder"
          : contextData.user?.user_type
      );
      data.append("requested_by_id", contextData.user?.uu_id ?? "");
      if (contextData.user?.user_type === "external") {
        const decoded = jwtDecode<TokenPayload>(
          contextData.user.access_token || ""
        );
        data.append("staack_holder_id", contextData.user?.org_unit_id ?? "");
        data.append("first_name", decoded?.first_name || "");
        data.append("last_name", decoded?.last_name || "");
        data.append("email_user_organization", decoded?.email || "");
        data.append(
          "organization_kebele",
          decoded?.organization?.address?.kebele || ""
        );
        data.append(
          "organization_zone",
          decoded?.organization?.address?.zone || ""
        );
        data.append(
          "organization_state",
          decoded?.organization?.address?.state || ""
        );
        data.append("organization_name", decoded?.organization?.name || "");
        data.append("organization_type", decoded?.organization?.type || "");
      } else {
        data.append(
          "staack_holder_id",
          selectedOrganization?.organization.uu_id ?? ""
        );
        data.append("first_name", selectedOrganization?.first_name || "");
        data.append("last_name", selectedOrganization?.last_name || "");
        data.append(
          "email_user_organization",
          selectedOrganization?.email || ""
        );
        data.append(
          "organization_kebele",
          selectedOrganization?.organization?.address?.kebele || ""
        );
        data.append(
          "organization_zone",
          selectedOrganization?.organization?.address?.zone || ""
        );
        data.append(
          "organization_state",
          selectedOrganization?.organization?.address?.state || ""
        );
        data.append(
          "organization_name",
          selectedOrganization?.organization?.name || ""
        );
        data.append(
          "organization_type",
          selectedOrganization?.organization?.type || ""
        );
      }

      try {
        const response = await createLegislativeDrafting(data);
        if (response.id) {
          navigate(`/legislative-drafting/${response.id}`);
        }
        showToast(t("LegislativeDraftSentSuccess") || "Legislative draft sent successfully!", "success");
        if (fileInputRefRequestLetter.current)
          fileInputRefRequestLetter.current.value = "";
        if (fileInputRefDraftInstruction.current)
          fileInputRefDraftInstruction.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegislativeDraft") || "Failed to send Legislative draft request.", "error");
      } finally {
        setSubmitting(false);
      }
    }
  };

  useEffect(() => {
    const token =
      contextData.token ??
      contextData?.user?.access_token ??
      contextData?.user?.user?.access_token;

    getStakeholders(token)
      .then((data) => {
        console.log("Stakeholders response:", data);

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : [];

        setOrganization(list.filter((item: any) => item?.organization));
      })
      .catch((error) => {
        console.error("Error fetching:", error);
        showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
      });
  }, [getStakeholders, contextData, showToast, t]);

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("RequestForLegislativeDrafting") || "Request for Legislative Drafting"}
        subtitle={t("LegislativeDraftingSubtitle") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl">
              <div className="">
                {contextData.user?.user_type !== "external" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Remark") || "Remark"}
                    </label>
                    <select
                      id="select-prosecutor"
                      className="appearance-none w-full p-2 border border-gray-50 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                      value={selectedOrganization?.id || ""}
                      onChange={(e) =>
                        setSelectedOrganization(
                          organization.find(
                            (employee: any) => employee.id === e.target.value
                          )
                        )
                      }
                    >
                      <option value="">{t("SelectOrganization") || "Select Organization"}</option>
                      {organization.map((emp: any) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.organization?.name}
                        </option>
                      ))}
                    </select>
                    <br />
                  </div>
                )}
                <DynamicFormInput
                  inputs={[
                    {
                      value: formState.request_reference_number,
                      type: "text",
                      label: t("RequestLetterReferenceNumber") || "Request Letter Reference Number",
                      name: "request_reference_number",
                      onChange: handleChange,
                      placeholder: "#123",
                    },
                  ]}
                />
                <br />
                <DynamicFormInput
                  inputs={[
                    {
                      value: formState.draft_instruction_summary,
                      type: "textarea",
                      label: t("DraftInstructionSummary") || "Draft Instruction Summary",
                      name: "draft_instruction_summary",
                      onChange: handleChange,
                      placeholder: t("DetailHere") || "Detail here ...",
                    },
                  ]}
                />
                <div className="grid gap-6 my-6 md:grid-cols-2">
                  <DynamicFormInput
                    inputs={[
                      {
                        type: "file",
                        label: t("RequestLetterRequired") || "Request Letter*",
                        name: "request_letter",
                        onChange: (file) =>
                          handleFileChange(file, "request_letter"),
                      },
                      {
                        type: "file",
                        label: t("DraftInstructionRequired") || "Draft Instruction*",
                        name: "draft_instruction_file",
                        onChange: (file) =>
                          handleFileChange(file, "draft_instruction_file"),
                      },
                    ]}
                  />
                </div>
              </div>
              <br />
              <div className="flex space-x-4 justify-between">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (fileInputRefRequestLetter.current)
                      fileInputRefRequestLetter.current.value = "";
                    if (fileInputRefDraftInstruction.current)
                      fileInputRefDraftInstruction.current.value = "";
                    navigate(-1);
                  }}
                >
                  {t("Cancel") || "Cancel"}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={() => console.log()}
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting") || "Submitting..."}
                    </div>
                  ) : (
                    t("Submit") || "Submit"
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("Note") || "Note"}
                text={t("NoteText") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}
                subtext={t("NoteSubtext") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold">{t("DocumentRequirements") || "Document Requirements"}</h2>
                    <p className="text-sm text-gray-500">
                      {t("DocumentRequirementsDescription") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}
                    </p>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("ConductLegalResearch") || "Conducting Legal Research"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("LegislativeDrafting") || "Legislative Drafting"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateLegislativeDraftingRequest;